/*  pending_trade_dates.tsx
    – "Dates needing attention" cards shared by the trades page (allocation view)
      and the settings/ops page (allocation + Geneva send). One card per pending
      date with a donut breakdown and the pending accounts, styled after the
      orders-page period summary (open_ended_order_period_summary.tsx).
      5 cards per row (wrapping beyond); the whole panel collapses from its header.
*/
import React, { useState } from 'react';
import { ActionIcon, Collapse, Divider, Group, Loader, Paper, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { PieChart } from '@mui/x-charts/PieChart';
import { TradeDate } from '../../types';
import { getPendingTradeDates } from './trades_permissions';

interface PendingTradeDatesProps {
    tradeDates: Record<string, TradeDate>;
    /** null → all accounts (admin/oper); otherwise scope to these accounts */
    accounts: string[] | null;
    /** ops view: also surface trades not yet sent to Geneva */
    showUnsent?: boolean;
    loading?: boolean;
    selectedDateId?: string;
    onSelectDate: (date: Date) => void;
}

// mantine green-6 / blue-6 / orange-6
const COLOR_DONE = '#40c057';
const COLOR_ALLOCATED = '#228be6';
const COLOR_PENDING = '#fd7e14';

const PendingTradeDates = ({ tradeDates, accounts, showUnsent = false, loading = false, selectedDateId, onSelectDate }: PendingTradeDatesProps) => {
    const [expanded, setExpanded] = useState(false);

    if (loading) {
        return (
            <Group gap="xs" mb="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">Checking pending trade dates…</Text>
            </Group>
        );
    }

    // Allocation is everyone's concern; the Geneva send is the ops team's job only.
    const rows = getPendingTradeDates(tradeDates, accounts)
        .filter((d) => showUnsent || d.unallocated > 0);
    if (rows.length === 0) return null;

    const totalUnallocated = rows.reduce((s, d) => s + d.unallocated, 0);
    const totalUnsent = rows.reduce((s, d) => s + d.unsent, 0);

    return (
        <Paper withBorder radius="md" p="xs" mb="md">
            <UnstyledButton w="100%" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} aria-label="Toggle pending trade dates">
                <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                        <IconAlertTriangle size={18} color="var(--mantine-color-orange-6)" />
                        <Text size="sm" fw={600}>
                            {rows.length} date{rows.length > 1 ? 's' : ''} pending
                        </Text>
                        <Text size="xs" c="dimmed">
                            {totalUnallocated > 0 ? `${totalUnallocated} to allocate` : ''}
                            {totalUnallocated > 0 && showUnsent && totalUnsent > 0 ? ' · ' : ''}
                            {showUnsent && totalUnsent > 0 ? `${totalUnsent} to send` : ''}
                        </Text>
                    </Group>
                    <ActionIcon component="div" variant="subtle" color="gray" size="sm">
                        {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                    </ActionIcon>
                </Group>
            </UnstyledButton>

            <Collapse expanded={expanded}>
                <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm" verticalSpacing="sm" mt="xs">
                    {rows.map((d) => {
                        const [dd, mm, yyyy] = d.dateId.split('-').map(Number);
                        const selected = d.dateId === selectedDateId;

                        // Partition the date's trades for the donut:
                        // ops view: sent (locked) / allocated-but-not-sent / not allocated
                        // trades view: allocated / not allocated
                        const sent = Math.max(0, d.totalRows - d.unsent);
                        const pendingAccounts = d.accounts.filter((a) => showUnsent || a.unallocated > 0);
                        const slices = (showUnsent
                            ? [
                                { id: 'sent', label: 'Sent to Geneva', value: sent, color: COLOR_DONE },
                                { id: 'allocated', label: 'Allocated, not sent', value: Math.max(0, d.allocated - sent), color: COLOR_ALLOCATED },
                                { id: 'unallocated', label: 'Not allocated', value: d.unallocated, color: COLOR_PENDING },
                            ]
                            : [
                                { id: 'allocated', label: 'Allocated', value: d.allocated, color: COLOR_DONE },
                                { id: 'unallocated', label: 'To allocate', value: d.unallocated, color: COLOR_PENDING },
                            ]
                        ).filter((s) => s.value > 0);

                        return (
                            <Paper
                                key={d.dateId}
                                p="xs"
                                radius="md"
                                withBorder
                                shadow="xs"
                                onClick={() => onSelectDate(new Date(yyyy, mm - 1, dd))}
                                style={{
                                    minWidth: 0,
                                    cursor: 'pointer',
                                    borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
                                }}
                            >
                                <Stack gap={4}>
                                    <Group justify="space-between" gap={8} wrap="nowrap">
                                        <Text size="sm" fw={700} lineClamp={1}>{d.dateId}</Text>
                                        <Text size="10px" c="dimmed" fw={600}>
                                            {d.totalRows} trade{d.totalRows === 1 ? '' : 's'}
                                        </Text>
                                    </Group>

                                    <PieChart
                                        series={[
                                            {
                                                data: slices.map(({ id, label, value }) => ({ id, label, value })),
                                                innerRadius: 20,
                                                outerRadius: 40,
                                                paddingAngle: 1,
                                            },
                                        ]}
                                        colors={slices.map((s) => s.color)}
                                        height={92}
                                        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                                        hideLegend
                                        skipAnimation
                                    />

                                    <Stack gap={2}>
                                        {slices.map((slice) => (
                                            <Group key={slice.id} justify="space-between" gap={6} wrap="nowrap">
                                                <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                                                    <span
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            backgroundColor: slice.color,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Text size="10px" lineClamp={1}>{slice.label}</Text>
                                                </Group>
                                                <Text size="10px" c="dimmed" fw={600}>{slice.value}</Text>
                                            </Group>
                                        ))}
                                    </Stack>

                                    {pendingAccounts.length > 0 && (
                                        <>
                                            <Divider my={2} />
                                            <Stack gap={2}>
                                                {pendingAccounts.map((a) => (
                                                    <Group key={a.accountId} justify="space-between" gap={6} wrap="nowrap">
                                                        <Text size="10px" fw={600} lineClamp={1}>{a.accountId}</Text>
                                                        <Text size="10px" c="dimmed" fw={600} style={{ flexShrink: 0 }}>
                                                            {a.unallocated > 0 ? `${a.unallocated} not allocated` : ''}
                                                            {a.unallocated > 0 && showUnsent && a.unsent > 0 ? ' · ' : ''}
                                                            {showUnsent && a.unsent > 0 ? `${a.unsent} not sent` : ''}
                                                        </Text>
                                                    </Group>
                                                ))}
                                            </Stack>
                                        </>
                                    )}
                                </Stack>
                            </Paper>
                        );
                    })}
                </SimpleGrid>
            </Collapse>
        </Paper>
    );
};

export default PendingTradeDates;
